import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, Briefcase } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav, { BREADCRUMB_ITEMS } from '../components/BreadcrumbNav';

// الإمارات السبع
const EMIRATES = [
  { 
    id: 'abu-dhabi', 
    name_ar: 'إمارة أبوظبي', 
    name_en: 'Abu Dhabi',
    color: '#10B981',
    disabled: true
  },
  { 
    id: 'dubai', 
    name_ar: 'إمارة دبي', 
    name_en: 'Dubai',
    color: '#3B82F6',
    disabled: true
  },
  { 
    id: 'sharjah', 
    name_ar: 'إمارة الشارقة', 
    name_en: 'Sharjah',
    color: '#8B5CF6',
    disabled: true
  },
  { 
    id: 'ajman', 
    name_ar: 'إمارة عجمان', 
    name_en: 'Ajman',
    color: '#F59E0B',
    disabled: true
  },
  { 
    id: 'umm-al-quwain', 
    name_ar: 'إمارة أم القيوين', 
    name_en: 'Umm Al Quwain',
    color: '#EC4899',
    disabled: true
  },
  { 
    id: 'ras-al-khaimah', 
    name_ar: 'إمارة رأس الخيمة', 
    name_en: 'Ras Al Khaimah',
    color: '#06B6D4',
    disabled: false,
    route: '/control/company-setup/ras-al-khaimah'
  },
  { 
    id: 'fujairah', 
    name_ar: 'إمارة الفجيرة', 
    name_en: 'Fujairah',
    color: '#EF4444',
    disabled: true
  },
];

export default function CompanySetup() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-indigo-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <DashboardHeader
        showBackButton={true}
        backPath="/control/company-setup/database"
      />

      {/* Breadcrumb with border */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/dashboard?view=cloudgm' },
            { label_ar: 'منصة العملاء', label_en: 'Customers Platform', path: '/dashboard?view=cloudgm&platform=customers' },
            { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
            { label_ar: 'تأسيس الشركات', label_en: 'Company Setup', path: '/control/company-setup' },
            { label_ar: 'قاعدة بيانات الرخص التجارية و الأوراق', label_en: 'Trade Licenses Database', path: '/control/company-setup/database' },
            { label_ar: 'دولة الإمارات العربية المتحدة', label_en: 'United Arab Emirates', path: null }
          ]} />
        </div>
      </div>

      {/* Title Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-white mb-2">
          {language === 'ar' ? 'تأسيس الشركات' : 'Company Setup'}
        </h1>
        <p className="text-[#71717A]">
          {language === 'ar' ? 'اختر الإمارة لعرض خيارات التأسيس' : 'Select an emirate to view setup options'}
        </p>
      </div>

      {/* Main Content */}
      <main className="relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Emirates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {EMIRATES.map((emirate, index) => (
              <motion.div
                key={emirate.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={!emirate.disabled ? { y: -5, scale: 1.02 } : {}}
                onClick={() => !emirate.disabled && emirate.route && navigate(emirate.route)}
                className={`group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all duration-300 text-center ${
                  emirate.disabled 
                    ? 'border-white/5 opacity-50 cursor-not-allowed' 
                    : 'border-white/10 hover:border-[#d4af37]/50 cursor-pointer hover:shadow-lg hover:shadow-[#d4af37]/5'
                }`}
                data-testid={`emirate-${emirate.id}`}
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: `${emirate.color}15` }}
                >
                  <MapPin className="h-8 w-8" style={{ color: emirate.color }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {language === 'ar' ? emirate.name_ar : emirate.name_en}
                </h3>
                {emirate.disabled ? (
                  <span className="text-xs text-[#71717A] bg-white/5 px-3 py-1 rounded-full">
                    {language === 'ar' ? 'قريباً' : 'Coming Soon'}
                  </span>
                ) : (
                  <span className="text-xs text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full">
                    {language === 'ar' ? 'متاح' : 'Available'}
                  </span>
                )}
              </motion.div>
            ))}
          </div>

          {/* Info Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-8 bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10"
          >
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#d4af37]" />
              {language === 'ar' ? 'ملاحظات' : 'Notes'}
            </h3>
            <ul className="space-y-3 text-sm text-[#A1A1AA]">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#d4af37]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin className="h-3 w-3 text-[#d4af37]" />
                </div>
                <span>{language === 'ar' ? 'اختر الإمارة المناسبة لعرض خيارات تأسيس الشركات المتاحة' : 'Select the appropriate emirate to view available company setup options'}</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#d4af37]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Building2 className="h-3 w-3 text-[#d4af37]" />
                </div>
                <span>{language === 'ar' ? 'كل إمارة لها جهات ترخيص مختلفة (المنطقة الحرة، الدائرة الاقتصادية)' : 'Each emirate has different licensing authorities (Free Zone, Economic Department)'}</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
